import React, { useContext, useMemo } from "react";
import _ from "lodash/fp";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import { AppBar, Box, Grid, Tab } from "@mui/material";
import { useFormContext } from "react-hook-form";

import { ProgramsContext } from "../../../programsProvider";
import { UserContext } from "../../../userProvider";

import {
  filterFieldsByObjectValues,
  filterOptionsByObjectValues,
} from "../../../services/formUtils";

import {
  useMemoCompare,
  useSessionStore,
  useStore,
} from "../../../services/hooks";

import { getTaskPermissions, sortByName } from "../../../services/utils";

import { FormGrid } from "../../../components";
import { RelationshipsTable } from "./RelationshipsTable";
import { ReferencesTab } from "./ReferencesTab";
import { StandardsTab } from "./StandardsTab";

import { metadataCardTabs } from "./constants";

const ITEM_WRITER_ROLE = "Item Writer";
const DEVELOPMENT_STATE = "Development";
const RESTRICTED_ASSIGNMENT_FIELDS = ["contentSpecialist", "itemWriter"];

const MetadataCard = ({ activeTasks, configuration, objectType, readonly }) => {
  const cardName = `${_.capitalize(objectType)}MetadataCard`;

  const [workspace, workspaceStore] = useSessionStore("workspace");
  const { selectedTab } = useMemoCompare(
    workspace?.cards.find((c) => c.i === cardName).state || {},
  );

  const programs = useContext(ProgramsContext);
  const user = useContext(UserContext);

  const { control, getValues, watch } = useFormContext();

  const fields = {
    item: ["item.content", "item.contentArea", "item.ethnicity", "item.region"],
    passage: [
      "passage.content",
      "passage.contentArea",
      "passage.ethnicity",
      "passage.region",
    ],
  };
  const watchedFields = useMemoCompare(watch(fields[objectType]));

  const [object] = useStore(objectType);

  const accessiblePrograms = useMemo(() => {
    return programs
      ?.filter((program) =>
        user?.programAccess.some(
          (accessibleProgram) => accessibleProgram?.program === program?.id,
        ),
      )
      .sort(sortByName)
      .map(({ id, name, keywords }) => ({
        id,
        label: name,
        value: name,
        keywords,
      }));
  }, [programs, user?.programAccess]);

  const permissions = useMemo(
    () => getTaskPermissions(activeTasks[objectType]),
    [activeTasks, objectType],
  );

  const tabs = useMemo(() => {
    return metadataCardTabs(permissions.includes("externalReview"));
  }, [permissions]);

  const shouldRestrictAssignmentFields = useMemo(() => {
    const reviewTypes = user?.programAccess?.find(
      (access) => access.program === object?.program?.id,
    )?.reviewTypes;
    return Boolean(
      !user?.superAdmin &&
        object?.developmentState === DEVELOPMENT_STATE &&
        reviewTypes?.length === 1 &&
        reviewTypes[0] === ITEM_WRITER_ROLE,
    );
  }, [
    object?.developmentState,
    object?.program?.id,
    user?.programAccess,
    user?.superAdmin,
  ]);

  const visibleFields = useMemo(() => {
    if (!selectedTab) return [];
    const allFields = tabs[objectType].find(
      (o) => o.label === selectedTab,
    )?.controls;
    return getValues(objectType) && configuration && allFields
      ? filterFieldsByObjectValues(getValues(objectType))(configuration)(
          allFields,
        )
      : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration, getValues, objectType, selectedTab, watchedFields]);

  if (!object || !getValues(`${objectType}.id`)) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        "& header": tabs[objectType].length === 1 ? { display: "none" } : {},
      }}
    >
      <TabContext value={selectedTab}>
        <AppBar color="transparent" elevation={1} position="static">
          <TabList
            variant="scrollable"
            onChange={workspaceStore.selectTab(cardName)}
          >
            {tabs[objectType].map(({ label }) => (
              <Tab key={label} label={label} value={label} />
            ))}
          </TabList>
        </AppBar>
        {configuration &&
          tabs[objectType].map(({ label }) => (
            <TabPanel
              key={label}
              sx={{ height: "100%", overflowY: "auto", padding: 1 }}
              value={label}
            >
              {label === "Relationships" && (
                <RelationshipsTable
                  key={`${objectType}-${object?.name}-relationships-table`}
                  objectName={object?.name}
                  objectType={objectType}
                  programs={programs}
                  programOptions={accessiblePrograms}
                  readonly={readonly}
                />
              )}
              {label === "References" && (
                <ReferencesTab objectType={objectType} readonly={readonly} />
              )}
              {label === "Standards" && (
                <StandardsTab objectType={objectType} readonly={readonly} />
              )}
              <Grid container spacing={2}>
                {visibleFields.map((path) => {
                  if (!configuration[path]) return null;
                  const {
                    disablePlaceholder = false,
                    options,
                    type,
                    ...props
                  } = configuration[path];
                  const gridItemProps = {
                    disablePlaceholder,
                    id: `${objectType}.${path}`,
                    name: `${objectType}.${path}`,
                    objectType: objectType,
                    options:
                      path === "keywords"
                        ? object?.program?.keywords
                            ?.sort((a, b) =>
                              a.value.localeCompare(b.value, "en", {
                                sensitivity: "base",
                              }),
                            )
                            .map(({ value }) => ({
                              label: value,
                              value: value,
                            }))
                        : ["multipleSelect", "select"].includes(type)
                          ? filterOptionsByObjectValues(getValues(objectType))(
                              configuration,
                            )(options)
                          : options,
                    type: type,
                    ...props,
                    readonly:
                      props.readonly ||
                      readonly ||
                      permissions.includes("externalReview") ||
                      (shouldRestrictAssignmentFields &&
                        RESTRICTED_ASSIGNMENT_FIELDS.includes(path)),
                  };
                  return (
                    <FormGrid
                      control={control}
                      key={`${label}-form-grid-${path}`}
                      {...gridItemProps}
                    />
                  );
                })}
              </Grid>
            </TabPanel>
          ))}
      </TabContext>
    </Box>
  );
};

export { MetadataCard };
